import type { CartItem } from 'store/checkout'
import type { ProductGroup } from 'types/product'
import { isSubscription } from '@utils/product'
import type { iProduct } from 'types/product'
import { isMenopauseSubscriptionServiceProduct } from 'lib/content/config'

export type MixedCartConflictType = 'has_subscription' | 'has_non_subscription' | 'has_investigation' | 'has_non_investigation' | 'has_multi_product'

export const MIXED_CART_MESSAGES: Record<MixedCartConflictType, string> = {
  has_subscription:
    'It looks like you already have a subscription product in your cart. Please complete your subscription purchase separately before adding any other items to your cart.',
  has_non_subscription:
    'It looks like you have a non-subscription product in your cart. Please remove the non-subscription item before adding any subscription products.',
  has_investigation:
    'It looks like you already have a test product in your cart. Please complete the purchase separately before adding other items to your cart.',
  has_non_investigation: 'Please complete the purchase before adding test items to your cart.',
  has_multi_product:
    'It looks like you already have treatments from one consultation in your cart. Please complete that purchase separately before adding other items to your cart.'
}

export const isInvestigationProduct = (productGroup?: ProductGroup | null) => !!productGroup?.inv

export const isSubscriptionItem = (item?: {
  parentId?: string
  subscription?: { _subscription_period?: string }
  variation?: ReadonlyArray<{ attribute: string; value: string } | null | undefined>
}): boolean => {
  // TODO: hash check as a stopgap. The subscription block is copied off the variant at
  // add-to-cart, but the reorder response rebuilds items without it, so a reordered plan
  // would look like a one-off. Products with pa_ep_frequency survive that path via the
  // scan below. This only rescues detection — the cadence label in Summary has the same
  // gap and no equivalent workaround, since a label can't be derived from the hash.
  // Both need the block on the reorder payload, or /api/products/enrich returning
  // variant-level data.
  if (isMenopauseSubscriptionServiceProduct(item?.parentId)) return true

  // The frequency attribute settles it where a product has one, matching isSubscription.
  // A subscription-type product keeps its recurring terms on the one-time variation too,
  // so reading the block first would sell a one-off as recurring.
  const frequency = item?.variation?.find(v => v?.attribute === 'pa_ep_frequency')
  if (frequency) return frequency.value !== 'one-time-purchase'

  return !!item?.subscription?._subscription_period
}

export const cartHasSubscription = (cart: CartItem[]) => cart.some(isSubscriptionItem)

export const cartHasInvestigation = (cart: CartItem[]) => cart.some(el => isInvestigationProduct(el.product_group))

export const requiresSingleItemCart = (cart: CartItem[]) => cartHasSubscription(cart) || cartHasInvestigation(cart)

const consultationKey = (item: CartItem) => (item.answer ? `${item.answer}|${item.patient ?? ''}` : null)

/** Answers and patient of the consultation two or more cart items were picked from, or null when there is none. */
export const getMultiProductKey = (cart: CartItem[]): string | null => {
  const keys = cart.map(consultationKey).filter((key): key is string => !!key)
  return keys.find((key, index) => keys.indexOf(key) !== index) ?? null
}

export const cartHasMultiProduct = (cart: CartItem[]) => getMultiProductKey(cart) !== null

/** A multi-product selection is ordered on its own, so any item from elsewhere breaks the cart. */
export const isMixedMultiProductCart = (cart: CartItem[]) => {
  const key = getMultiProductKey(cart)
  return key !== null && cart.some(item => consultationKey(item) !== key)
}

/** Whether the cart holds something that has to be bought on its own. */
export const isExclusiveCart = (cart: CartItem[]) => requiresSingleItemCart(cart) || cartHasMultiProduct(cart)

/** Whether the cart mixes an item that has to be bought on its own with other items. */
export const breaksExclusiveCart = (cart: CartItem[]) => (cart.length > 1 && requiresSingleItemCart(cart)) || isMixedMultiProductCart(cart)

export const SINGLE_ITEM_CART_TRIMMED_MESSAGE = "Subscriptions and tests have to be bought on their own, so we've removed the other items from your cart."

/**
 * Cuts a cart that breaks the single-item rule down to one item: a subscription before a test,
 * and the newer side's item when both sides hold one. A valid cart comes back unchanged.
 */
export const resolveSingleItemCart = (cart: CartItem[], isNewer: (item: CartItem) => boolean): CartItem[] => {
  if (cart.length < 2 || !requiresSingleItemCart(cart)) return cart
  const pick = (matches: (item: CartItem) => boolean) => cart.find(item => matches(item) && isNewer(item)) ?? cart.find(matches)
  const keep = pick(isSubscriptionItem) ?? pick(item => isInvestigationProduct(item.product_group))
  return keep ? [keep] : cart
}

type MixedCartParams = {
  cartLength: number
  hasSubscriptionInCart: boolean
  hasInvestigationInCart: boolean
  hasMultiProductInCart: boolean
  isAddingSubscription: boolean
  isAddingInvestigation: boolean
}

export const getMixedCartConflict = ({
  cartLength,
  hasSubscriptionInCart,
  hasInvestigationInCart,
  hasMultiProductInCart,
  isAddingSubscription,
  isAddingInvestigation
}: MixedCartParams): MixedCartConflictType | null => {
  if (hasSubscriptionInCart) return 'has_subscription'
  if (hasInvestigationInCart) return 'has_investigation'
  if (hasMultiProductInCart) return 'has_multi_product'
  if (cartLength > 0 && isAddingSubscription) return 'has_non_subscription'
  if (cartLength > 0 && isAddingInvestigation) return 'has_non_investigation'
  return null
}

export const getMixedCartMessage = (conflict: MixedCartConflictType | null) => (conflict ? MIXED_CART_MESSAGES[conflict] : '')

/** Conflict when the cart already contains a subscription, investigation or multi-product selection (cart page proceed blocked). */
export const getSingleItemCartConflict = (cart: CartItem[]): MixedCartConflictType | null =>
  getMixedCartConflict({
    cartLength: cart.length,
    hasSubscriptionInCart: cartHasSubscription(cart),
    hasInvestigationInCart: cartHasInvestigation(cart),
    hasMultiProductInCart: cartHasMultiProduct(cart),
    isAddingSubscription: false,
    isAddingInvestigation: false
  })

export const getSingleItemCartMessage = (cart: CartItem[]) => getMixedCartMessage(getSingleItemCartConflict(cart))

export const getAddToCartConflict = (cart: CartItem[], product: Pick<iProduct, 'product_group'>, variant: iProduct['variations'][0] | null) =>
  getMixedCartConflict({
    cartLength: cart.length,
    hasSubscriptionInCart: cartHasSubscription(cart),
    hasInvestigationInCart: cartHasInvestigation(cart),
    hasMultiProductInCart: cartHasMultiProduct(cart),
    isAddingSubscription: isSubscription(variant),
    isAddingInvestigation: isInvestigationProduct(product.product_group)
  })
